import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Crown, ShieldCheck, Loader2, CheckCircle2, Star, HeartHandshake,
  Activity, Brain, Baby, Salad, PhoneCall, CalendarCheck, Sparkles,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { TalkToTeamButton } from '../../components/learner/TalkToTeamButton';

const HERO_IMG =
  'https://images.unsplash.com/photo-1710897869539-a48e3d69a8d7?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200';

const PILLARS = [
  { icon: Activity, title: 'Body', desc: 'Prenatal yoga, kegels & birthing-ball routines to open your pelvis and prepare for labor.' },
  { icon: Brain, title: 'Mind', desc: 'Hypnobirthing & labor pain management — reduce fear and stay calm through contractions.' },
  { icon: Baby, title: 'Internal', desc: 'Womb engineering & garbhsamwad to create optimal space and bond deeply with your baby.' },
  { icon: Salad, title: 'Diet & 1:1', desc: 'A personalised diet plan with 1:1 support on every pregnancy question you have.' },
];

const DELIVERABLE_ICONS = {
  onboarding_call: PhoneCall,
  live_yoga: CalendarCheck,
  dietician: Salad,
  weekly_plan: Sparkles,
  care_manager: HeartHandshake,
};

const OUTCOMES = [
  { stat: '90%', label: 'Natural deliveries under 7 hrs active labor' },
  { stat: '2×', label: 'Faster recovery postpartum' },
  { stat: '7,500+', label: 'Mothers guided worldwide' },
];

const TESTIMONIALS = [
  { name: 'Meghna A.', text: 'The breathing techniques got me through labor calmly. My care team felt like family.', src: 'Google · 4.9★' },
  { name: 'Priya S.', text: 'Personalised classes for my trimester and a dietician who actually listened. Worth every rupee.', src: 'Trustpilot · 4.7★' },
  { name: 'Aishwarya B.', text: 'From anxious to confident. The 1:1 onboarding call set everything up perfectly.', src: 'Google · 4.9★' },
];

const FAQS = [
  { q: 'What exactly do I get with the program?', a: 'A 1:1 onboarding call, live small-batch prenatal yoga with certified instructors, 1:1 dietician support, a personalised weekly plan and WhatsApp care-manager check-ins — plus the SoulAI coach, class recordings and baby tracker as included tooling.' },
  { q: 'Can I try before I commit?', a: 'Yes. Book a single live trial class, meet your instructor, and continue with the program only if you love it. 100% money-back on the trial if it is not for you.' },
  { q: 'Is it safe in my trimester?', a: 'Every batch is risk-matched and trimester-specific. First-trimester mothers get gentle breathing-led sessions under expert guidance (assuming no complications).' },
  { q: 'Can I cancel anytime?', a: 'Absolutely. Billed monthly — cancel anytime and your care team stays with you till the end of the paid month.' },
];

const money = (n, sym) => `${sym}${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const TrustStrip = () => (
  <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs sm:text-sm text-slate-600" data-testid="elite-trust-strip">
    <span className="inline-flex items-center gap-1 font-medium"><HeartHandshake className="h-4 w-4 text-primary" />7,500+ mothers</span>
    <span className="inline-flex items-center gap-1 font-medium"><Star className="h-4 w-4 fill-amber-400 text-amber-400" />4.9 Google (135+)</span>
    <span className="inline-flex items-center gap-1 font-medium"><Star className="h-4 w-4 fill-emerald-500 text-emerald-500" />4.7 Trustpilot (120+)</span>
  </div>
);

export default function EliteProgram() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/pricing/catalog', { params: { placement: 'gold_membership' } });
      setQuote(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load the program details');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const monthly = quote?.items?.monthly;
  const tillDelivery = quote?.items?.till_delivery;
  const program = monthly?.program;
  const sym = monthly?.razorpay_currency === 'USD' ? '$' : '₹';
  const tax = monthly?.tax;

  const joinProgram = async () => {
    if (!quote || !monthly) return;
    setPaying(true);
    try {
      const orderRes = await api.post('/api/payment/create-order', { quote_id: quote.quote_id, method: 'razorpay', item_key: 'monthly' });
      const { order_id, razorpay_key_id } = orderRes.data;
      if (!order_id || !razorpay_key_id || !window.Razorpay) { toast.error('Payment gateway unavailable. Please try again.'); setPaying(false); return; }
      const rzp = new window.Razorpay({
        key: razorpay_key_id, amount: monthly.razorpay_amount_paise, currency: monthly.razorpay_currency, order_id,
        name: 'Soul Sanskar Academy', description: 'Elite Prenatal Care Program — Monthly',
        prefill: { name: user?.name || '', email: user?.email || '', contact: user?.phone || '' },
        theme: { color: '#295c56' },
        modal: { ondismiss: () => setPaying(false) },
        handler: async (resp) => {
          try {
            const v = await api.post('/api/payment/verify', { order_id, payment_id: resp.razorpay_payment_id, signature: resp.razorpay_signature });
            if (v.data.verified) {
              toast.success('Welcome to Elite! Your care specialist will call you within 24 hours.');
              if (refreshUser) await refreshUser();
              navigate('/learner/dashboard');
            } else toast.error('Payment verification failed. Contact support.');
          } catch { toast.error('Verification error. Contact support if charged.'); }
          setPaying(false);
        },
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start payment');
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-4" data-testid="elite-program-loading">
        <Skeleton className="h-56 rounded-3xl" />
        <Skeleton className="h-40 rounded-2xl" />
        <Skeleton className="h-40 rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto pb-28 sm:pb-10" data-testid="elite-program-page">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-b-3xl sm:rounded-3xl sm:mt-4">
        <img src={HERO_IMG} alt="Expecting mother preparing for a calm birth" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-slate-900/60 to-primary/50" />
        <div className="relative px-5 sm:px-10 py-10 sm:py-16 text-white">
          <Badge className="bg-white/15 text-white border-white/25 backdrop-blur-sm mb-4">
            <Crown className="h-3.5 w-3.5 mr-1 text-amber-300" />Elite Prenatal Care Program
          </Badge>
          <h1 className="font-heading text-3xl sm:text-5xl font-semibold leading-tight max-w-2xl">
            Prepare for a calm, natural delivery — guided every step by your own care team.
          </h1>
          <p className="mt-3 text-sm sm:text-base text-white/85 max-w-xl">
            A complete birth-preparation system with certified prenatal experts — live classes,
            1:1 dietician support and daily guidance, right from your home.
          </p>
          <div className="mt-6"><TrustStrip /></div>
        </div>
      </section>

      <div className="px-4 sm:px-6 space-y-10 mt-8">
        {/* Price card */}
        <Card className="rounded-3xl border-amber-200 bg-gradient-to-br from-amber-50 via-white to-rose-50 shadow-sm" data-testid="elite-price-card">
          <CardContent className="p-5 sm:p-7 grid sm:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <p className="font-heading text-xl font-semibold text-slate-900">{program?.name || 'Elite Prenatal Care Program'}</p>
              <p className="text-sm text-slate-600 mt-1">{program?.tagline}</p>
              {monthly && (
                <div className="mt-4 flex items-end gap-2">
                  {monthly.mrp_formatted && (
                    <span className="text-sm text-slate-400 line-through mb-1">{monthly.mrp_formatted}</span>
                  )}
                  <span className="font-heading text-4xl font-semibold text-slate-900" data-testid="elite-price">
                    {monthly.price_formatted}
                  </span>
                  <span className="text-sm text-slate-600 mb-1">/ month</span>
                </div>
              )}
              {tax && !tax.international && (
                <p className="text-xs text-slate-500 mt-1" data-testid="elite-tax-line">
                  + {tax.gst_pct}% GST · Total {monthly.total_formatted}
                </p>
              )}
              {tax?.international && (
                <p className="text-xs text-slate-500 mt-1">Taxes included where applicable</p>
              )}
            </div>
            <div className="flex flex-col gap-2 w-full sm:w-64">
              <Button onClick={joinProgram} disabled={paying || !monthly} className="w-full h-12 text-base bg-primary hover:bg-primary/90" data-testid="elite-join-btn">
                {paying ? <Loader2 className="h-5 w-5 animate-spin" /> : (<><Crown className="h-4 w-4 mr-2" />Join the program</>)}
              </Button>
              <Button variant="outline" onClick={() => navigate('/learner/trial-classes')} className="w-full h-11 border-primary/30 text-primary hover:bg-primary/5" data-testid="elite-trial-btn">
                Book a trial class first
              </Button>
              <p className="text-[11px] text-slate-500 text-center flex items-center justify-center gap-1 mt-1">
                <ShieldCheck className="h-3 w-3" />Secured by Razorpay · Cancel anytime
              </p>
            </div>
          </CardContent>
        </Card>

        {/* How it works — pillars */}
        <section data-testid="elite-pillars">
          <h2 className="font-heading text-2xl font-semibold text-slate-900 text-center">A complete system, not just classes</h2>
          <p className="text-sm text-slate-600 text-center mt-1 max-w-lg mx-auto">Eight specialised techniques across four pillars, tailored to your trimester and baby position.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            {PILLARS.map((p) => (
              <Card key={p.title} className="rounded-2xl border-slate-200 hover:border-primary/40 hover:shadow-sm transition-all duration-200">
                <CardContent className="p-5">
                  <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center mb-3">
                    <p.icon className="h-5 w-5 text-primary" />
                  </div>
                  <p className="font-heading font-semibold text-slate-900">{p.title}</p>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">{p.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* What's included */}
        {program && (
          <section data-testid="elite-deliverables">
            <h2 className="font-heading text-2xl font-semibold text-slate-900 text-center">Your care team, every week</h2>
            <div className="grid sm:grid-cols-2 gap-3 mt-6">
              {program.deliverables?.map((d) => {
                const Icon = DELIVERABLE_ICONS[d.key] || CheckCircle2;
                return (
                  <div key={d.key} className="flex gap-3 rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="h-9 w-9 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                      <Icon className="h-4.5 w-4.5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-slate-900">{d.title}</p>
                      <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">{d.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            {program.included_tooling?.length > 0 && (
              <div className="mt-4 rounded-2xl bg-slate-50 border border-slate-200 p-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Included tooling</p>
                <div className="flex flex-wrap gap-2">
                  {program.included_tooling.map((t) => (
                    <Badge key={t.key} variant="secondary" className="bg-white border-slate-200 text-slate-700 font-normal">
                      <CheckCircle2 className="h-3 w-3 mr-1 text-primary" />{t.title}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Outcomes */}
        <section className="grid grid-cols-3 gap-3" data-testid="elite-outcomes">
          {OUTCOMES.map((o) => (
            <div key={o.label} className="rounded-2xl bg-primary/5 border border-primary/10 p-4 text-center">
              <p className="font-heading text-2xl sm:text-3xl font-semibold text-primary">{o.stat}</p>
              <p className="text-[11px] sm:text-xs text-slate-600 mt-1 leading-snug">{o.label}</p>
            </div>
          ))}
        </section>

        {/* Testimonials */}
        <section data-testid="elite-testimonials">
          <h2 className="font-heading text-2xl font-semibold text-slate-900 text-center">Real mothers, real births</h2>
          <div className="grid sm:grid-cols-3 gap-4 mt-6">
            {TESTIMONIALS.map((t) => (
              <Card key={t.name} className="rounded-2xl border-slate-200">
                <CardContent className="p-5">
                  <div className="flex gap-0.5 mb-2">{[...Array(5)].map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />)}</div>
                  <p className="text-sm text-slate-700 leading-relaxed">“{t.text}”</p>
                  <p className="text-xs text-slate-500 mt-3 font-medium">{t.name} · {t.src}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Compare */}
        <section data-testid="elite-compare">
          <h2 className="font-heading text-2xl font-semibold text-slate-900 text-center">Choose your journey</h2>
          <div className="grid sm:grid-cols-3 gap-4 mt-6">
            <Card className="rounded-2xl border-slate-200">
              <CardContent className="p-5">
                <p className="font-heading font-semibold text-slate-900">Trial class</p>
                <p className="font-heading text-2xl font-semibold mt-2">Try one class</p>
                <p className="text-xs text-slate-600 mt-2">One live 45-min class · meet your instructor · 100% money-back</p>
                <Button variant="outline" className="w-full mt-4 border-primary/30 text-primary" onClick={() => navigate('/learner/trial-classes')} data-testid="elite-compare-trial-btn">Book trial</Button>
              </CardContent>
            </Card>
            <Card className="rounded-2xl border-primary ring-1 ring-primary/30 relative">
              <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-amber-100 text-amber-900 border-amber-200">Most popular</Badge>
              <CardContent className="p-5">
                <p className="font-heading font-semibold text-slate-900">Elite Prenatal Care Program</p>
                <p className="font-heading text-2xl font-semibold mt-2">{monthly?.price_formatted}<span className="text-sm text-slate-500 font-normal">/mo</span></p>
                <p className="text-xs text-slate-600 mt-2">Everything above · live classes · 1:1 dietician · weekly plan · care manager</p>
                <Button className="w-full mt-4 bg-primary hover:bg-primary/90" onClick={joinProgram} disabled={paying} data-testid="elite-compare-join-btn">Join now</Button>
              </CardContent>
            </Card>
            <Card className="rounded-2xl border-slate-200">
              <CardContent className="p-5">
                <p className="font-heading font-semibold text-slate-900">Elite Till Delivery</p>
                <p className="font-heading text-2xl font-semibold mt-2">{tillDelivery?.price_from_formatted || '—'}<span className="text-sm text-slate-500 font-normal">/mo from</span></p>
                <p className="text-xs text-slate-600 mt-2">All of it till your due date · dedicated support · flexible slots</p>
                <TalkToTeamButton plan="elite_till_delivery" label="Request a callback" size="default" className="w-full mt-4 border-primary/30 text-primary" testId="elite-compare-td-btn" />
              </CardContent>
            </Card>
          </div>
        </section>

        {/* FAQ */}
        <section data-testid="elite-faq">
          <h2 className="font-heading text-2xl font-semibold text-slate-900 text-center">Common questions</h2>
          <div className="mt-6 space-y-2 max-w-2xl mx-auto">
            {FAQS.map((f, i) => (
              <div key={f.q} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                <button type="button" onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between gap-3 p-4 text-left" data-testid={`elite-faq-${i}`}>
                  <span className="font-medium text-sm text-slate-900">{f.q}</span>
                  <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${openFaq === i ? 'rotate-90' : ''}`} />
                </button>
                {openFaq === i && <p className="px-4 pb-4 -mt-1 text-sm text-slate-600 leading-relaxed">{f.a}</p>}
              </div>
            ))}
          </div>
        </section>

        {/* Closing CTA */}
        <section className="rounded-3xl bg-slate-900 text-white p-7 sm:p-10 text-center" data-testid="elite-closing-cta">
          <Crown className="h-8 w-8 text-amber-300 mx-auto mb-3" />
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold">Give your baby the calmest start</h2>
          <p className="text-sm text-white/80 mt-2 max-w-md mx-auto">Join thousands of mothers who prepared with confidence. Your care specialist calls within 24 hours of joining.</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={joinProgram} disabled={paying || !monthly} className="bg-white text-slate-900 hover:bg-white/90 h-12 px-8 text-base" data-testid="elite-closing-join-btn">
              {paying ? <Loader2 className="h-5 w-5 animate-spin" /> : (<>Join the Elite program</>)}
            </Button>
            <Button variant="outline" onClick={() => navigate('/learner/trial-classes')} className="border-white/30 text-white hover:bg-white/10 h-12 px-8" data-testid="elite-closing-trial-btn">
              Start with a trial class
            </Button>
          </div>
          <p className="text-[11px] text-white/60 mt-4 flex items-center justify-center gap-1"><ShieldCheck className="h-3 w-3" />Payment processed securely by Razorpay · A GST receipt is emailed to you.</p>
        </section>
      </div>

      {/* Sticky mobile CTA */}
      <div className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-slate-200 p-3 flex items-center gap-3" data-testid="elite-sticky-cta">
        <div className="flex-1 min-w-0">
          <p className="font-heading font-semibold text-slate-900 leading-tight">{monthly?.price_formatted}<span className="text-xs text-slate-500 font-normal">/mo</span></p>
          {tax && !tax.international && <p className="text-[10px] text-slate-500">+{tax.gst_pct}% GST · {monthly?.total_formatted}</p>}
        </div>
        <Button onClick={joinProgram} disabled={paying || !monthly} className="bg-primary hover:bg-primary/90 shrink-0" data-testid="elite-sticky-join-btn">
          {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Crown className="h-4 w-4 mr-1.5" />Join program</>)}
        </Button>
      </div>
    </div>
  );
}
